import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, getClient, createClient, updateClient, deleteClient, getInvoices, getSellerList } from '../utils/api.js';
import { Card, Button, Input, Badge } from '../components/legacy-ui.js';
import { AppPageHeader, AppTable, AppModalShell, AppModalFooter } from '../components/app-ui.js';
import { TagsInput } from '../components/ui/tags-input.js';
import { formatCurrency } from '../utils/formatters.js';
import { Plus, Pencil, Trash2, ChevronLeft, ChevronRight, Search, Eye, Receipt } from 'lucide-react';
import InvoiceViewModal from '../components/InvoiceViewModal.js';
import { useAuth } from '../context/AuthContext.js';
import { Helmet } from 'react-helmet';
import { companyPageTitle } from '../utils/company.js';
import toast from 'react-hot-toast';
import { LoadingState } from '../components/motion.js';

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path
      fill="currentColor"
      d="M12.04 2C6.58 2 2.13 6.45 2.13 12c0 1.77.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2zM12.05 20.13h-.01c-1.48 0-2.92-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.32a8.02 8.02 0 01-1.22-4.38c0-4.41 3.59-8 8.01-8s8.01 3.59 8.01 8-3.59 8-8.01 8zm4.44-5.64c-.24-.12-1.45-.72-1.67-.8s-.39-.12-.55.12-.63.8-.78.96-.29.18-.55.06c-.25-.12-1.06-.39-2.02-1.25-.75-.67-1.25-1.5-1.4-1.75s-.12-.24 0-.36c.11-.11.24-.29.36-.43s.16-.24.24-.43.12-.24.06-.43c-.06-.18-.55-1.32-.75-1.82s-.4-.42-.55-.42h-.48c-.18 0-.47.06-.71.3s-.9.88-.9 2.15.92 2.49 1.04 2.67.9 2.33 3.01 3.25.55.12.88.1.98-.06 1.13-.24.63-1.04.75-1.29.12-.25.06-.37z"
    />
  </svg>
);

const ClientsModule = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = !user?.role || user.role === 'admin';
  const canCreateClients = isAdmin || Boolean(user?.permissions?.createClients);
  const [clients, setClients] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', credit: 0, nicknames: [], seller: '' });
  const [loading, setLoading] = useState(true);
  const [editingClient, setEditingClient] = useState(null);
  const [isInvoicesModalOpen, setIsInvoicesModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientInvoices, setClientInvoices] = useState([]);
  const [clientInvoicesLoading, setClientInvoicesLoading] = useState(false);
  const [clientInvoicesPage, setClientInvoicesPage] = useState(1);
  const [selectedInvoiceDetail, setSelectedInvoiceDetail] = useState(null);

  const [showCreditLogsModal, setShowCreditLogsModal] = useState(false);
  const [selectedClientForLogs, setSelectedClientForLogs] = useState(null);
  const [creditLogsLoading, setCreditLogsLoading] = useState(false);

  // Pagination & Search State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const itemsPerPage = 10;
  const invoicesPerPage = 6;

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to page 1 on new search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchClients = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchTerm,
      };
      const res = await getClients(params);
      setClients(res.data.data);
      setTotalPages(res.data.totalPages);
      setCurrentPage(res.data.currentPage);
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
  }, [user, currentPage, debouncedSearchTerm]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    const loadSellers = async () => {
      try {
        const res = await getSellerList();
        setSellers(res.data.data || []);
      } catch (error) {
        console.error('Error fetching sellers:', error);
      }
    };
    loadSellers();
  }, [user, isAdmin]);

  const sellerLabel = (seller) => {
    if (!seller) return 'Sin asignar';
    if (typeof seller === 'object') {
      return seller.fullName || seller.username || 'Sin asignar';
    }
    const found = sellers.find((s) => s._id === seller);
    return found ? found.fullName || found.username : 'Sin asignar';
  };

  const handleAddNew = () => {
    setEditingClient(null);
    setFormData({ name: '', phone: '', credit: 0, nicknames: [], seller: '' });
    setShowForm(true);
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      phone: client.phone?.startsWith('+58') ? client.phone.slice(3) : client.phone || '',
      credit: client.credit || 0,
      nicknames: client.nicknames || [],
      seller: client.seller?._id || client.seller || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (clientId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este cliente?')) {
      try {
        await deleteClient(clientId);
        toast.success("Cliente eliminado", { position: "top-center" });
        fetchClients();
      } catch (error) {
        console.error("Error deleting client:", error);
        toast.error(error.response?.data?.error || "No se pudo eliminar el cliente", {
          position: "top-center",
        });
      }
    }
  };

  const handleOpenClientInvoices = async (client) => {
    setSelectedClient(client);
    setIsInvoicesModalOpen(true);
    setClientInvoicesLoading(true);
    setClientInvoicesPage(1);
    try {
      const res = await getInvoices({ client: client._id });
      const invoices = (res.data?.data || []).filter(
        (invoice) => String(invoice.client?._id || invoice.client) === String(client._id),
      );
      setClientInvoices(invoices);
    } catch (error) {
      console.error("Error fetching client invoices:", error);
      setClientInvoices([]);
    } finally {
      setClientInvoicesLoading(false);
    }
  };

  const handleCloseClientInvoices = () => {
    setIsInvoicesModalOpen(false);
    setSelectedClient(null);
    setClientInvoices([]);
    setClientInvoicesPage(1);
    setSelectedInvoiceDetail(null);
  };

  const handleCloseCreditLogs = () => {
    setShowCreditLogsModal(false);
    setSelectedClientForLogs(null);
    setCreditLogsLoading(false);
  };

  const handleOpenCreditLogs = async (client) => {
    setSelectedClientForLogs(client);
    setShowCreditLogsModal(true);
    setCreditLogsLoading(true);
    try {
      const { data } = await getClient(client._id);
      if (data?.success && data.data) {
        setSelectedClientForLogs(data.data);
      }
    } catch (error) {
      console.error('Error al cargar logs de crédito:', error);
    } finally {
      setCreditLogsLoading(false);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingClient(null);
    setFormData({ name: '', phone: '', credit: 0, nicknames: [], seller: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dataToSave = {
      ...formData,
      phone: formData.phone ? `+58${formData.phone.replace(/\D/g, '')}` : undefined,
      nicknames: formData.nicknames.filter(Boolean),
      credit: Number(formData.credit) || 0,
      seller: formData.seller || null,
    };

    try {
      if (editingClient) {
        await updateClient(editingClient._id, dataToSave);
        toast.success("Cliente actualizado", { position: "top-center" });
      } else {
        await createClient(dataToSave);
        toast.success("Cliente creado", { position: "top-center" });
      }
      resetForm();
      fetchClients();
    } catch (error) {
      console.error("Error saving client: ", error);
      toast.error(error.response?.data?.error || "No se pudo guardar el cliente", {
        position: "top-center",
      });
    }
  };

  const ClientForm = (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Input label="Nombre Completo" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
      <Input
        label="Teléfono (🇻🇪 +58)"
        value={formData.phone.replace(/\D/g, '').replace(/^(\d{3})(\d{0,7})$/, (_, p1, p2) => p2 ? `${p1}-${p2}` : p1)}
        onChange={e => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
        inputMode="numeric"
        maxLength={11}
      />
      <div className="md:col-span-2 space-y-2">
        <TagsInput
          label="Apodos"
          placeholder="Apodo (Presiona Enter o Coma)"
          value={formData.nicknames}
          onChange={tags => setFormData({ ...formData, nicknames: tags })}
        />
      </div>
      <Input label="Crédito Inicial ($)" type="number" value={formData.credit} onChange={e => setFormData({ ...formData, credit: e.target.value })} />
      {isAdmin && (
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Vendedor</label>
          <select
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            value={formData.seller}
            onChange={(e) => setFormData({ ...formData, seller: e.target.value })}
          >
            <option value="">Sin asignar</option>
            {sellers.map((seller) => (
              <option key={seller._id} value={seller._id}>
                {seller.fullName || seller.username}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="md:col-span-2 flex gap-2 justify-end mt-4">
        <Button type="button" variant="secondary" onClick={resetForm}>Cancelar</Button>
        <Button type="submit">Guardar</Button>
      </div>
    </form>
  );

  const paginatedClientInvoices = clientInvoices.slice(
    (clientInvoicesPage - 1) * invoicesPerPage,
    clientInvoicesPage * invoicesPerPage,
  );
  const clientInvoicesTotalPages = Math.max(
    1,
    Math.ceil(clientInvoices.length / invoicesPerPage),
  );

  return (
    <>
      <Helmet>
        <title>{companyPageTitle("Clientes")}</title>
      </Helmet>
      <div className="app-page">
        {/* --- Top, non-scrollable part --- */}
        <div className="app-section-head">
          <AppPageHeader
            title={isAdmin ? "Clientes" : "Mis clientes"}
            subtitle={
              isAdmin
                ? "Gestión de clientes registrados"
                : "Clientes asignados a tu cuenta"
            }
            actions={
              canCreateClients ? (
                <Button onClick={handleAddNew}><Plus size={16} /> Nuevo</Button>
              ) : null
            }
          />
          <div className="relative mt-4 mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Buscar por nombre, teléfono o apodo..."
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm placeholder-gray-400"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* --- Middle, scrollable part --- */}
        <div className="app-content">
          {loading ? <LoadingState label="Cargando clientes" /> : (
            <>
              {/* Vista Móvil (Cards) */}
              <div className="md:hidden space-y-4">
                {clients.map(client => (
                  <Card key={client._id} className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-bold text-gray-900">{client.name}</h3>
                        <p className="text-sm text-gray-500">{client.phone || 'Sin teléfono'}</p>
                      </div>
                      <Badge type={client.credit > 0 ? 'success' : 'info'}>
                        Cred: {formatCurrency(client.credit)}
                      </Badge>
                    </div>
                    {client.nicknames?.length > 0 && (
                      <p className="text-xs text-gray-500 mt-2">Apodos: {client.nicknames.join(', ')}</p>
                    )}
                    {isAdmin && (
                      <p className="text-xs text-indigo-600 mt-1">
                        Vendedor: {sellerLabel(client.seller)}
                      </p>
                    )}
                    <div className="flex gap-2 justify-end mt-4">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenCreditLogs(client)} title="Ver historial de crédito">
                        <Receipt size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleOpenClientInvoices(client)} title="Ver facturas">
                        <Eye size={16} />
                      </Button>
                      {isAdmin && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(client)}><Pencil size={16} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(client._id)}><Trash2 size={16} /></Button>
                        </>
                      )}
                      {client.phone && client.phone.length === 13 && (
                        <Button variant="ghost" size="sm" onClick={() => window.open(`https://wa.me/${client.phone.replace(/\D/g, '')}`, '_blank', 'noopener,noreferrer')}>
                          <WhatsAppIcon className="h-5 w-5 text-green-600" />
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>

              {/* Vista PC (Tabla) */}
              <div className="hidden md:block">
                <AppTable
                  columns={[
                    { key: "name", title: "Nombre" },
                    { key: "nicknames", title: "Apodos" },
                    { key: "phone", title: "Teléfono" },
                    ...(isAdmin
                      ? [{ key: "seller", title: "Vendedor" }]
                      : []),
                    { key: "orders", title: "Pedidos", align: "center" },
                    { key: "credit", title: "Crédito", align: "right" },
                    { key: "actions", title: "Acciones", align: "center" },
                  ]}
                >
                    {clients.map(client => (
                      <tr key={client._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-3.5 text-sm font-medium text-gray-900">{client.name}</td>
                        <td className="px-6 py-3.5 text-sm text-gray-500">{client.nicknames?.join(', ')}</td>
                        <td className="px-6 py-3.5 text-sm text-gray-500">{client.phone || '—'}</td>
                        {isAdmin && (
                          <td className="px-6 py-3.5 text-sm text-gray-600">
                            {sellerLabel(client.seller)}
                          </td>
                        )}
                        <td className="px-6 py-3.5 text-sm text-center text-gray-600">{client.orderCount || 0}</td>
                        <td className="px-6 py-3.5 text-sm text-right text-gray-900 font-medium">{formatCurrency(client.credit)}</td>
                        <td className="px-6 py-3.5 text-center">
                          <div className="flex gap-1 justify-center">
                            <Button variant="ghost" size="sm" onClick={() => handleOpenCreditLogs(client)} title="Ver historial de crédito">
                              <Receipt size={16} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleOpenClientInvoices(client)} title="Ver facturas">
                              <Eye size={16} />
                            </Button>
                            {isAdmin && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => handleEdit(client)}><Pencil size={16} /></Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(client._id)}><Trash2 size={16} /></Button>
                              </>
                            )}
                            {client.phone && client.phone.length === 13 && (
                              <Button variant="ghost" size="sm" onClick={() => window.open(`https://wa.me/${client.phone.replace(/\D/g, '')}`, '_blank', 'noopener,noreferrer')}>
                                <WhatsAppIcon className="h-5 w-5 text-green-600" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </AppTable>
              </div>
            </>
          )}
        </div>

        {/* --- Bottom, non-scrollable part --- */}
        <div className="app-footer">
          <div className="flex justify-center items-center gap-3">
            <Button variant="secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
              <ChevronLeft size={16} />
            </Button>
            <span className="text-sm text-gray-600 font-medium">Página {currentPage} de {totalPages}</span>
            <Button variant="secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      </div>

      <AppModalShell
        open={showForm}
        onClose={resetForm}
        title={`${editingClient ? 'Editar' : 'Registrar'} Cliente`}
        widthClass="max-w-lg"
      >
        {ClientForm}
      </AppModalShell>

      {isInvoicesModalOpen && selectedClient && (
        <AppModalShell
          open={isInvoicesModalOpen}
          onClose={handleCloseClientInvoices}
          title={`Facturas de ${selectedClient.name}`}
          widthClass="max-w-4xl"
          footer={
            <AppModalFooter>
              <span className="text-sm text-gray-500">Total: {clientInvoices.length}</span>
              <div className="flex flex-col-reverse sm:flex-row gap-2 w-full sm:w-auto">
                <Button variant="secondary" className="w-full sm:w-auto" onClick={handleCloseClientInvoices}>
                  Cerrar
                </Button>
                <Button
                  className="w-full sm:w-auto"
                  onClick={() => {
                    handleCloseClientInvoices();
                    navigate("/invoices", { state: { clientId: selectedClient._id } });
                  }}
                >
                  <Plus size={16} /> Nueva factura
                </Button>
              </div>
            </AppModalFooter>
          }
        >
            <div className="reveal-host space-y-4">
              {clientInvoicesLoading ? (
                <LoadingState count={4} label="Cargando facturas" />
              ) : paginatedClientInvoices.length > 0 ? (
                <>
                  <div className="md:hidden space-y-3">
                    {paginatedClientInvoices.map((invoice) => {
                      const isPaid = invoice.pendingAmount <= 0.01;
                      return (
                        <Card key={invoice._id} className="p-3.5">
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div>
                              <p className="font-bold text-gray-900"># {invoice.invoiceNumber}</p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {new Date(invoice.createdAt).toLocaleDateString("es-VE")}
                              </p>
                            </div>
                            <Badge type={isPaid ? "success" : "warning"}>
                              {isPaid ? "Pagada" : "Pendiente"}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Total</p>
                              <p className="font-semibold text-gray-800 mt-0.5">
                                {formatCurrency(invoice.totalAmount)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Pendiente</p>
                              <p className="font-semibold text-red-500 mt-0.5">
                                {formatCurrency(invoice.pendingAmount)}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full mt-3 justify-center"
                            onClick={() => setSelectedInvoiceDetail(invoice)}
                          >
                            <Eye size={16} className="mr-2 shrink-0" />
                            Ver factura
                          </Button>
                        </Card>
                      );
                    })}
                  </div>

                  <div className="hidden md:block">
                    <AppTable
                      columns={[
                        { key: "invoice", title: "Factura" },
                        { key: "date", title: "Fecha" },
                        { key: "total", title: "Total", align: "right" },
                        { key: "pending", title: "Pendiente", align: "right" },
                        { key: "status", title: "Estado", align: "center" },
                        { key: "view", title: "Ver", align: "center" },
                      ]}
                    >
                      {paginatedClientInvoices.map((invoice) => (
                        <tr key={invoice._id} className="hover:bg-gray-50">
                          <td className="font-medium text-gray-900"># {invoice.invoiceNumber}</td>
                          <td className="text-gray-500">
                            {new Date(invoice.createdAt).toLocaleDateString("es-VE")}
                          </td>
                          <td className="text-right font-semibold text-gray-800">
                            {formatCurrency(invoice.totalAmount)}
                          </td>
                          <td className="text-right font-semibold text-red-500">
                            {formatCurrency(invoice.pendingAmount)}
                          </td>
                          <td className="text-center">
                            <Badge type={invoice.pendingAmount <= 0.01 ? "success" : "warning"}>
                              {invoice.pendingAmount <= 0.01 ? "Pagada" : "Pendiente"}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Ver factura"
                              onClick={() => setSelectedInvoiceDetail(invoice)}
                            >
                              <Eye size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </AppTable>
                  </div>
                </>
              ) : (
                <p className="text-center text-gray-500 py-8">
                  Este cliente no tiene facturas registradas.
                </p>
              )}

              {!clientInvoicesLoading && clientInvoices.length > 0 && clientInvoicesTotalPages > 1 && (
                <div className="flex justify-center items-center gap-3 pt-1">
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setClientInvoicesPage((page) => Math.max(1, page - 1))
                    }
                    disabled={clientInvoicesPage === 1}
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <span className="text-sm text-gray-600 font-medium">
                    Página {clientInvoicesPage} de {clientInvoicesTotalPages}
                  </span>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setClientInvoicesPage((page) =>
                        Math.min(clientInvoicesTotalPages, page + 1),
                      )
                    }
                    disabled={clientInvoicesPage >= clientInvoicesTotalPages}
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              )}
            </div>
        </AppModalShell>
      )}

      <InvoiceViewModal
        open={Boolean(selectedInvoiceDetail)}
        onClose={() => setSelectedInvoiceDetail(null)}
        invoice={selectedInvoiceDetail}
        titlePrefix="Factura"
        mode="readonly"
        zClass="z-[80]"
        creditLogs={selectedClient?.creditLogs}
      />

      {showCreditLogsModal && selectedClientForLogs && (
        <AppModalShell
          open={showCreditLogsModal}
          onClose={handleCloseCreditLogs}
          title={`Logs de crédito — ${selectedClientForLogs.name}`}
          widthClass="max-w-3xl"
          zClass="z-[80]"
          fitContent
          footer={
            <AppModalFooter>
              <span className="text-sm text-gray-600">
                Crédito actual:{' '}
                <span className="font-semibold text-indigo-600">
                  {formatCurrency(selectedClientForLogs.credit ?? 0)}
                </span>
              </span>
              <Button variant="secondary" className="w-full sm:w-auto" onClick={handleCloseCreditLogs}>
                Cerrar
              </Button>
            </AppModalFooter>
          }
        >
              {creditLogsLoading ? (
                <LoadingState count={4} label="Cargando movimientos" />
              ) : selectedClientForLogs.creditLogs && selectedClientForLogs.creditLogs.length > 0 ? (
                <>
                  <div className="md:hidden space-y-3">
                    {[...selectedClientForLogs.creditLogs]
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((log, idx) => (
                        <Card key={`${log.date}-${idx}`} className="p-3.5">
                          <p className="text-xs text-gray-500">
                            {new Date(log.date).toLocaleString("es-VE")}
                          </p>
                          <p className="font-medium text-gray-800 mt-1">{log.note || log.action}</p>
                          {log.invoiceNumber && (
                            <p className="text-sm text-gray-500 mt-1">Factura # {log.invoiceNumber}</p>
                          )}
                          <div className="flex justify-between mt-2 text-sm">
                            <span
                              className={`font-semibold ${log.delta >= 0 ? "text-emerald-600" : "text-red-500"}`}
                            >
                              {log.delta >= 0 ? "+" : "-"} {formatCurrency(Math.abs(log.delta))}
                            </span>
                            <span className="font-semibold text-indigo-600">
                              Saldo: {formatCurrency(log.balanceAfter || 0)}
                            </span>
                          </div>
                        </Card>
                      ))}
                  </div>
                  <div className="hidden md:block">
                    <AppTable
                      columns={[
                        { key: "date", title: "Fecha" },
                        { key: "action", title: "Acción" },
                        { key: "invoice", title: "Factura" },
                        { key: "delta", title: "Delta", align: "right" },
                        { key: "balance", title: "Saldo", align: "right" },
                      ]}
                    >
                      {[...selectedClientForLogs.creditLogs]
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .map((log, idx) => (
                          <tr key={`${log.date}-${idx}`} className="hover:bg-gray-50">
                            <td className="text-gray-600 whitespace-nowrap">
                              {new Date(log.date).toLocaleString("es-VE")}
                            </td>
                            <td className="text-gray-700">{log.note || log.action}</td>
                            <td className="text-gray-500">
                              {log.invoiceNumber ? `# ${log.invoiceNumber}` : "—"}
                            </td>
                            <td
                              className={`text-right font-semibold ${log.delta >= 0 ? "text-emerald-600" : "text-red-500"}`}
                            >
                              {log.delta >= 0 ? "+" : "-"} {formatCurrency(Math.abs(log.delta))}
                            </td>
                            <td className="text-right font-semibold text-indigo-600">
                              {formatCurrency(log.balanceAfter || 0)}
                            </td>
                          </tr>
                        ))}
                    </AppTable>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center min-h-[50vh] md:min-h-[10rem] text-center px-4">
                  <Receipt className="w-12 h-12 text-gray-300 mb-3" />
                  <p className="text-gray-600 font-medium">No hay movimientos de crédito registrados.</p>
                  <p className="text-sm text-gray-500 mt-2">
                    El crédito disponible de este cliente es{' '}
                    <span className="font-semibold text-indigo-600">
                      {formatCurrency(selectedClientForLogs.credit ?? 0)}
                    </span>
                    .
                  </p>
                </div>
              )}
        </AppModalShell>
      )}
    </>
  );
};

export default ClientsModule;